import { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../../../api';

export const useDashboardData = (user, filtros) => {
    const [pages, setPages] = useState([]);
    const [posts, setPosts] = useState([]);
    const [comentarios, setComentarios] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [singlePageHistory, setSinglePageHistory] = useState(null);

    const fetchData = useCallback(async () => {
        if (!user?.cliente_id) return;
        setLoading(true);
        try {
            const requests = [
                apiClient.get(`/api/pages/${user.cliente_id}`),
                apiClient.get(`/api/posts/${user.cliente_id}`),
                apiClient.get(`/api/comments/${user.cliente_id}`)
            ];

            if (filtros.pagina && filtros.pagina !== 'todas') {
                requests.push(apiClient.get(`/api/history/page/${filtros.pagina}`));
            } else {
                setSinglePageHistory(null);
            }

            const responses = await Promise.all(requests);
            
            setPages(responses[0].data);
            setPosts(responses[1].data);
            setComentarios(responses[2].data);

            if (responses.length > 3) {
                const historyData = responses[3].data;
                setSinglePageHistory({
                    followers_count: historyData.followers_count.slice(-1)[0],
                    media_count: historyData.media_count.slice(-1)[0] 
                });
            }
        } catch (err) {
            console.error("Falha ao carregar dados:", err);
        } finally {
            setLoading(false);
        }
    }, [user?.cliente_id, filtros.pagina]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const mediaTypes = useMemo(() => {
        const types = new Set(posts.map(p => p.media_product_type).filter(Boolean));
        return Array.from(types);
    }, [posts]);

    const postsFiltradosGeral = useMemo(() => {
        return posts.filter(p => {
            const matchPage = filtros.pagina === 'todas' || p.page_id == filtros.pagina;
            const matchType = filtros.tipo === 'todos' || p.media_product_type === filtros.tipo;
            let matchDate = true;
            
            if (filtros.viewInicio || filtros.viewFim) {
                const postDate = new Date(p.timestamp);
                if (filtros.viewInicio) {
                    const start = new Date(filtros.viewInicio);
                    if (postDate < start) matchDate = false;
                }
                if (filtros.viewFim) {
                    const end = new Date(filtros.viewFim);
                    end.setHours(23, 59, 59, 999); 
                    if (postDate > end) matchDate = false;
                }
            }
            return matchPage && matchType && matchDate;
        });
    }, [posts, filtros]);
    const pageMetrics = useMemo(() => {
        if (filtros.pagina !== 'todas' && singlePageHistory) {
            return {
                followers_count: singlePageHistory.followers_count,
                media_count: postsFiltradosGeral.length 
            };
        }

        const totalFollowers = pages.reduce((acc, page) => acc + (page.followers_count || 0), 0);
        
        const totalMediaInPeriod = postsFiltradosGeral.length;

        return {
            followers_count: totalFollowers,
            media_count: totalMediaInPeriod
        };
    }, [filtros.pagina, singlePageHistory, pages, postsFiltradosGeral]);

    // Cálculo de Engajamento (Likes, Comentários, etc)
    const aggregatedMetrics = useMemo(() => {
        const initialMetrics = { likes: 0, comments: 0, shares: 0, saves: 0, views: 0, plays: 0, reach: 0 };
        
        if (filtros.post !== 'todos') {
            const postSelecionado = posts.find(p => p.post_id == filtros.post);
            return postSelecionado ? {
                likes: postSelecionado.like_count || 0,
                comments: postSelecionado.comment_count || 0,
                shares: postSelecionado.share_count || 0,
                saves: postSelecionado.save_count || 0,
                views: postSelecionado.view_count || postSelecionado.video_view_count || 0,
                plays: postSelecionado.play_count || 0,
                reach: postSelecionado.reach || 0 
            } : initialMetrics;
        }

        return postsFiltradosGeral.reduce((acc, post) => {
            acc.likes += post.like_count || 0;
            acc.comments += post.comment_count || 0;
            acc.shares += post.share_count || 0;
            acc.saves += post.save_count || 0;
            acc.views += (post.view_count || post.video_view_count || 0);
            acc.plays += (post.play_count || 0);
            acc.reach += (post.reach || 0);
            return acc;
        }, initialMetrics);
    }, [posts, postsFiltradosGeral, filtros.post]);

    return {
        pages,
        posts,
        comentarios,
        loading,
        pageMetrics,
        mediaTypes,
        fetchData,
        aggregatedMetrics,
        postsFiltradosGeral
    };
};